import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  AlertTriangle,
  Mail,
  Send,
  Clock,
  ChevronDown,
  ChevronRight,
  Building2,
  User,
  Award,
  RefreshCw,
  Calendar,
  CheckCircle,
  History,
  Settings
} from 'lucide-react';

const ExpiryReminders = () => {
  usePageTitle('Expiry Reminders');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [expiryWindow, setExpiryWindow] = useState('30');
  const [expandedCompanies, setExpandedCompanies] = useState({});
  
  // Send dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [sending, setSending] = useState(false);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/expiry-reminders/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/expiry-reminders/companies?expiry_window=${expiryWindow}`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load expiring certificates');
    } finally {
      setLoading(false);
    }
  }, [expiryWindow]);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const toggleCompanyExpanded = (companyId) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  const openSendDialog = (company) => {
    setSelectedCompany(company);
    setSelectedStudents(company.students.map(s => s.student_id));
    setSendDialogOpen(true);
  };

  const toggleStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSendReminder = async () => {
    if (!selectedCompany) return;
    
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API_URL}/expiry-reminders/send`, {
        company_id: selectedCompany.company_id,
        student_ids: selectedStudents,
        expiry_window: parseInt(expiryWindow)
      });
      
      toast.success(`Reminder sent to ${selectedCompany.company_name}`);
      setSendDialogOpen(false);
      setSelectedCompany(null);
      
      // Refresh the list to update "last reminded" status
      fetchCompanies();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send reminder';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDaysLabel = (daysLeft) => {
    if (daysLeft === null) return '-';
    if (daysLeft < 0) {
      return `${Math.abs(daysLeft)} days ago`;
    }
    return `${daysLeft} days`;
  };

  // Access check
  if (!['admin', 'training_admin'].includes(user?.role)) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">Only administrators and training admins can access this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Expiry Reminders
            </h1>
            <p className="text-slate-500 text-sm">Send certificate expiry notifications to companies</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/expiry-reminder-history')}>
            <History className="w-4 h-4 mr-2" />
            View History
          </Button>
          <Button variant="outline" onClick={() => navigate('/expiry-reminder-settings')}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button variant="outline" onClick={() => { fetchSummary(); fetchCompanies(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card className={expiryWindow === '30' ? 'ring-2 ring-[#2C9DE1]' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Expiring in 30 Days</p>
                  <p className="text-2xl font-bold text-amber-600">{summary.expiring_30_days}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-300" />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-2 w-full"
                onClick={() => setExpiryWindow('30')}
              >
                View
              </Button>
            </CardContent>
          </Card>
          <Card className={expiryWindow === '60' ? 'ring-2 ring-[#2C9DE1]' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Expiring in 60 Days</p>
                  <p className="text-2xl font-bold text-yellow-600">{summary.expiring_60_days}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-300" />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-2 w-full"
                onClick={() => setExpiryWindow('60')}
              >
                View
              </Button>
            </CardContent>
          </Card>
          <Card className={expiryWindow === '90' ? 'ring-2 ring-[#2C9DE1]' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Expiring in 90 Days</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.expiring_90_days}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-300" />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-2 w-full"
                onClick={() => setExpiryWindow('90')}
              >
                View
              </Button>
            </CardContent>
          </Card>
          <Card className={expiryWindow === '-1' ? 'ring-2 ring-red-500' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Already Expired</p>
                  <p className="text-2xl font-bold text-red-600">{summary.already_expired}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-300" />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-2 w-full text-red-600 hover:text-red-700"
                onClick={() => setExpiryWindow('-1')}
              >
                View & Notify
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Active</p>
                  <p className="text-2xl font-bold text-green-600">
                    {(summary.expiring_30_days || 0) + (summary.expiring_60_days || 0) + (summary.expiring_90_days || 0)}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Showing certificates:</span>
              <Select value={expiryWindow} onValueChange={setExpiryWindow}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Expiring in 30 days</SelectItem>
                  <SelectItem value="60">Expiring in 60 days</SelectItem>
                  <SelectItem value="90">Expiring in 90 days</SelectItem>
                  <SelectItem value="-1">Already Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-slate-500">
              {companies.length} {companies.length === 1 ? 'company' : 'companies'} with {expiryWindow === '-1' ? 'expired' : 'expiring'} certificates
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Companies List */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {expiryWindow === '-1' ? 'Companies with Expired Certificates' : 'Companies with Expiring Certificates'}
          </CardTitle>
          <CardDescription>
            {expiryWindow === '-1' 
              ? 'These certificates have already expired. Send a notification to arrange renewal training.'
              : 'Click on a company to see student details, then send a reminder email'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#2C9DE1]" />
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-4" />
              <p className="text-slate-500">
                {expiryWindow === '-1' 
                  ? 'No expired certificates found'
                  : `No certificates expiring in the next ${expiryWindow} days`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {companies.map((company) => (
                <Collapsible
                  key={company.company_id}
                  open={expandedCompanies[company.company_id]}
                  onOpenChange={() => toggleCompanyExpanded(company.company_id)}
                >
                  <div className="border rounded-lg">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-[#2C9DE1]/10 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-[#2C9DE1]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-900">{company.company_name}</h3>
                              <Badge variant="outline" className="text-xs">
                                {company.certificate_count} {company.certificate_count === 1 ? 'certificate' : 'certificates'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              {company.primary_contact ? (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {company.primary_contact}
                                </span>
                              ) : (
                                <span className="text-amber-600 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  No primary contact
                                </span>
                              )}
                              {company.company_email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {company.company_email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {company.last_reminded && (
                            <div className="text-right text-xs text-slate-400">
                              <div>Last reminded:</div>
                              <div>{formatDate(company.last_reminded)}</div>
                            </div>
                          )}
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSendDialog(company);
                            }}
                            disabled={!company.company_email}
                            className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Send Reminder
                          </Button>
                          {expandedCompanies[company.company_id] ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t px-4 py-3 bg-slate-50">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Student</TableHead>
                              <TableHead>Course</TableHead>
                              <TableHead>Certificate #</TableHead>
                              <TableHead>Expiry Date</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {company.students.flatMap(student =>
                              student.certificates.map(cert => {
                                const daysLeft = getDaysUntilExpiry(cert.expiry_date);
                                return (
                                  <TableRow key={cert.certificate_id}>
                                    <TableCell className="font-medium">{student.student_name}</TableCell>
                                    <TableCell>{cert.course_name}</TableCell>
                                    <TableCell className="font-mono text-sm">{cert.certificate_number}</TableCell>
                                    <TableCell>{formatDate(cert.expiry_date)}</TableCell>
                                    <TableCell>
                                      <Badge className={
                                        daysLeft < 0 ? 'bg-red-100 text-red-700' :
                                        daysLeft <= 14 ? 'bg-red-100 text-red-700' :
                                        daysLeft <= 30 ? 'bg-amber-100 text-amber-700' :
                                        'bg-blue-100 text-blue-700'
                                      }>
                                        {formatDaysLabel(daysLeft)}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Reminder Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Send Expiry Reminder</DialogTitle>
            <DialogDescription>
              Send a reminder email to {selectedCompany?.company_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-sm">
                  <div><strong>To:</strong> {selectedCompany.company_email}</div>
                  <div><strong>Primary Contact:</strong> {selectedCompany.primary_contact || 'Not set'}</div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Select students to include:</label>
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {selectedCompany.students.map(student => (
                    <div
                      key={student.student_id}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                      onClick={() => toggleStudent(student.student_id)}
                    >
                      <Checkbox
                        checked={selectedStudents.includes(student.student_id)}
                        onCheckedChange={() => toggleStudent(student.student_id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{student.student_name}</div>
                        <div className="text-xs text-slate-500">
                          {student.certificates.length} expiring {student.certificates.length === 1 ? 'certificate' : 'certificates'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="text-sm text-slate-500">
                {selectedStudents.length} of {selectedCompany.students.length} students selected
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendReminder}
              disabled={sending || selectedStudents.length === 0}
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
            >
              {sending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reminder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpiryReminders;
